<template>
  <Disclosure
    as="nav"
    class="md:hidden absolute top-0 right-0 z-[998] bg-white dark:bg-onyx"
    v-slot="{ open }"
  >
    <div class="px-4 mx-auto max-w-7xl md:px-6 lg:px-8">
      <div class="flex justify-between h-16">
        <div class="flex items-center -mr-2 md:hidden">
          <!-- Mobile menu button -->
          <DisclosureButton
            class="bg-white dark:bg-onyx inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:text-white dark:hover:bg-[#171717] focus:outline-none trans"
          >
            <span class="sr-only">Open main menu</span>
            <MenuIcon v-if="!open" class="block w-6 h-6" aria-hidden="true" />
            <XIcon v-else class="block w-6 h-6" aria-hidden="true" />
          </DisclosureButton>
        </div>
      </div>
    </div>
    <DisclosurePanel v-if="auth" class="w-screen h-screen md:hidden">
      <div class="pt-2 pb-3 space-y-1">
        <a
          v-for="item in navigation"
          :key="item.name"
          :href="item.href"
          :class="[
            item.current
              ? 'bg-[#e4e6ec] dark:bg-[#171717] dark:text-white border-fire text-tolopea'
              : 'border-transparent text-gray-600 dark:text-white hover:bg-gray-50 dark:hover:bg-[#171717] dark:hover:border-[#171717] hover:border-gray-300 hover:text-gray-800',
            'block pl-3 pr-4 py-2 border-l-4 text-base font-medium trans',
          ]"
          :aria-current="item.current ? 'page' : undefined"
        >
          {{ item.name }}
        </a>
      </div>
      <div class="pt-4 pb-3 border-t border-mute">
        <div class="flex items-center px-4">
          <div class="flex-shrink-0">
            <img class="w-10 h-10 rounded-full" :src="user.imageUrl" alt="" />
          </div>
          <div class="ml-3">
            <div class="text-base font-medium text-gray-800 dark:text-white">
              {{ user.name }}
            </div>
            <div class="text-sm font-medium text-gray-500 dark:text-mute">
              {{ user.email }}
            </div>
          </div>
          <button
            type="button"
            class="flex-shrink-0 p-1 ml-auto text-gray-400 bg-white rounded-full hover:text-fire focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-fire trans"
          >
            <span class="sr-only">View notifications</span>
            <BellIcon class="w-6 h-6" aria-hidden="true" />
          </button>
        </div>
        <div class="mt-3 space-y-1">
          <a
            v-for="item in userNavigation"
            :key="item.name"
            :href="item.href"
            :class="[
              item.current
                ? 'bg-[#e4e6ec] dark:bg-[#171717] dark:text-white border-fire text-tolopea'
                : 'border-transparent text-gray-600 dark:text-white hover:bg-gray-50 dark:hover:bg-[#171717] dark:hover:border-[#171717] hover:border-gray-300 hover:text-gray-800',
              'block pl-3 pr-4 py-2 border-l-4 text-base font-medium trans',
            ]"
          >
            {{ item.name }}
          </a>
        </div>
        <div
          class="flex flex-row items-center justify-between px-4 pt-4 mt-6 border-t border-mute"
        >
          <ModeSwitch />
          <LangMenu :current="lang" />
        </div>
      </div>
    </DisclosurePanel>
    <DisclosurePanel v-else class="w-screen h-screen md:hidden">
      <div class="px-5 pt-2 pb-3 space-y-1">
        <a
          v-for="item in navigationSimple"
          :key="item.name"
          :target="item.target ? item.target : '_self'"
          :href="item.href"
          :class="[
            item.current
              ? 'bg-[#e4e6ec] border-fire text-tolopea'
              : 'border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800',
            'block pl-3 pr-4 py-2 border-l-4 text-base font-medium trans',
          ]"
          :aria-current="item.current ? 'page' : undefined"
        >
          {{ item.name }}
        </a>
      </div>
      <div class="px-6 pt-2 pb-3 ml-2 text-center border-t border-gray-200">
        <div class="mt-3 space-y-1">
          <LangMenu :current="lang" />
        </div>
      </div>
    </DisclosurePanel>
  </Disclosure>
</template>

<script>
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
} from "@headlessui/vue";
import { BellIcon, MenuIcon, XIcon } from "@heroicons/vue/outline";
import { useI18n } from "vue-i18n";
import LangMenu from "./LangMenu";
import ModeSwitch from "./ModeSwitch";
import { computed, onMounted, ref } from "vue";

export default {
  name: "MobilePanel",
  components: {
    ModeSwitch,
    LangMenu,
    Disclosure,
    DisclosureButton,
    DisclosurePanel,
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    BellIcon,
    MenuIcon,
    XIcon,
  },
  props: {
    auth: {
      type: Boolean,
      default: false,
    },
    current: {
      type: Number,
      default: 1,
    },
  },
  setup(props) {
    const { t } = useI18n();

    const navigation = ref([
      { name: t("menu.dashboard"), href: "/dashboard", current: true, id: 1 },
      {
        name: t("menu.faq"),
        href: "https://facecoalition.com/faq",
        target: "_blank",
        current: false,
        id: 4,
      },
      {
        name: t("menu.privacy"),
        href: "https://facecoalition.com/privacy",
        target: "_blank",
        current: false,
        id: 3,
      },
      {
        name: t("menu.help"),
        href: "https://helpdesk.facecoalition.com/",
        target: "_blank",
        current: false,
        id: 6,
      },
      {
        name: t("menu.resources"),
        href: "https://facecoalition.com/resources",
        target: "_blank",
        current: false,
        id: 5,
      },
    ]);
    const userNavigation = ref([
      {
        name: t("menu.profile"),
        href: "/user/profile",
        current: false,
        id: 97,
      },
      /*{name: t('menu.settings'), href: '/settings', current: false, id: 98},*/
      { name: t("menu.logout"), href: "/logout", current: false, id: 99 },
    ]);
    const navigationSimple = ref([
      { name: t("menu.home"), href: "/", current: true, id: 1 },
      { name: t("menu.login"), href: "/login", current: false, id: 2 },
      {
        name: t("menu.faq"),
        href: "https://facecoalition.com/faq",
        target: "_blank",
        current: false,
        id: 4,
      },
      {
        name: t("menu.privacy"),
        href: "https://facecoalition.com/privacy",
        target: "_blank",
        current: false,
        id: 3,
      },
      {
        name: t("menu.help"),
        href: "https://helpdesk.facecoalition.com/",
        target: "_blank",
        current: false,
        id: 6,
      },
      {
        name: t("menu.resources"),
        href: "https://facecoalition.com/resources",
        target: "_blank",
        current: false,
        id: 5,
      },
    ]);

    const user = ref({
      name: "",
      email: "",
      imageUrl: "",
    });

    onMounted(() => {
      navigationSimple.value.forEach((item) => {
        item.current = false;
        if (item.id === props.current) {
          item.current = true;
        }
      });
      if (props.auth) {
        fetchUser();
      }
    });

    const fetchUser = async () => {
      try {
        const { data } = await axios.post("/me");

        user.value = {
          name: data.data.name,
          email: data.data.email,
          imageUrl: data.data.image,
        };
      } catch (err) {}
    };

    const lang = computed(() =>
      window.currentLang ? window.currentLang : "en"
    );

    return {
      t,
      lang,
      user,
      navigation,
      navigationSimple,
      userNavigation,
    };
  },
};
</script>
